import React, { useState, Component } from 'react';
import { CheckCircle, Circle, PaperPlaneTilt } from '@phosphor-icons/react';
import { cn, getOfferProgress, setOfferProgress } from '../../lib/utils';
interface NextStepsChecklistProps {
  offerId: string;
  landlordName: string;
  landlordPhone?: string;
  listingTitle?: string;
  tenantName?: string;
  onProgressChange?: () => void;
}
/**
 * Interactive 3-item checklist embedded inside ReceivedOfferCard when the
 * offer is in 'saved' state. Persists state to localStorage via the offer
 * progress store.
 *
 * Item 1 (Vezi datele de contact) is implicit — for a saved offer, the
 * contact panel is already unlocked above the checklist, so this is always
 * rendered as completed (no toggle).
 *
 * Items 2 + 3 are user-toggled. Item 2 also has a primary WhatsApp CTA
 * that auto-checks itself on click.
 */
export function NextStepsChecklist({
  offerId,
  landlordName,
  landlordPhone,
  listingTitle,
  tenantName,
  onProgressChange
}: NextStepsChecklistProps) {
  const initial = getOfferProgress(offerId);
  const [whatsappSent, setWhatsappSent] = useState(initial.whatsappSent);
  const [viewingScheduled, setViewingScheduled] = useState(initial.viewingScheduled);
  const [contractSigned, setContractSigned] = useState(initial.contractSigned);
  const [paymentMade, setPaymentMade] = useState(initial.paymentMade);
  const [movedIn, setMovedIn] = useState(initial.movedIn);
  const updateWhatsapp = (next: boolean) => {
    setWhatsappSent(next);
    setOfferProgress(offerId, {
      whatsappSent: next
    });
    onProgressChange?.();
  };
  const updateViewing = (next: boolean) => {
    setViewingScheduled(next);
    setOfferProgress(offerId, {
      viewingScheduled: next
    });
    onProgressChange?.();
  };
  const updateContract = (next: boolean) => {
    setContractSigned(next);
    setOfferProgress(offerId, {
      contractSigned: next
    });
    onProgressChange?.();
  };
  const updatePayment = (next: boolean) => {
    setPaymentMade(next);
    setOfferProgress(offerId, {
      paymentMade: next
    });
    onProgressChange?.();
  };
  const updateMovedIn = (next: boolean) => {
    setMovedIn(next);
    setOfferProgress(offerId, {
      movedIn: next
    });
    onProgressChange?.();
  };
  const handleWhatsApp = () => {
    const greeting = tenantName ? `Bună ${landlordName}, sunt ${tenantName} de pe Izio` : `Bună ${landlordName}, sunt pe Izio`;
    const ref = listingTitle ? ` și sunt interesat(ă) de ${listingTitle}.` : '.';
    const msg = `${greeting}${ref} Aș putea să te sun să discutăm mai multe?`;
    const phone = (landlordPhone || '').replace(/[^\d+]/g, '');
    const url = phone ? `https://wa.me/${phone.replace(/^\+/, '')}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    if (!whatsappSent) updateWhatsapp(true);
  };
  const completedManualSteps = [whatsappSent, viewingScheduled, contractSigned, paymentMade, movedIn].filter(Boolean).length;
  return <div className="rounded-2xl bg-dark-3 border border-dark-4 p-4 space-y-3" data-id="element-509">
      <div className="flex items-center gap-2 text-xs font-medium text-light-3 uppercase tracking-wider" data-id="element-510">
        Ce urmează?
      </div>

      <ul className="space-y-2.5" data-id="element-511">
        {/* Item 1 — always done */}
        <li className="flex items-start gap-3" data-id="element-512">
          <CheckCircle size={20} weight="fill" className="text-lime-1 shrink-0 mt-0.5" aria-hidden="true" data-id="element-513" />
          <span className="text-sm text-light-2 flex-1 leading-snug" data-id="element-514">
            Vezi datele de contact
            <span className="text-light-4" data-id="element-515"> · vizibile mai sus</span>
          </span>
        </li>

        {/* Item 2 — whatsapp */}
        <li className="flex items-start gap-3" data-id="element-516">
          <button type="button" onClick={() => updateWhatsapp(!whatsappSent)} className="shrink-0 mt-0.5 outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer" aria-label={whatsappSent ? 'Bifează ca neefectuat' : 'Bifează: contactează proprietarul'} data-id="element-517">
            {whatsappSent ? <CheckCircle size={20} weight="fill" className="text-lime-1" data-id="element-518" /> : <Circle size={20} className="text-light-4" data-id="element-519" />}
          </button>
          <div className="flex-1 min-w-0" data-id="element-520">
            <p className={cn('text-sm leading-snug', whatsappSent ? 'text-light-3 line-through' : 'text-light-2')} data-id="element-521">
              Contactează proprietarul
            </p>
            <button type="button" onClick={handleWhatsApp} className="inline-flex items-center gap-1.5 mt-2 h-8 px-3 rounded-md bg-lime-5 hover:bg-lime-4 border border-lime-4 text-lime-1 text-xs font-medium transition-colors cursor-pointer" data-id="element-522">
              <PaperPlaneTilt size={12} weight="fill" data-id="element-523" />
              Trimite mesaj WhatsApp
            </button>
          </div>
        </li>

        {/* Item 3 — viewing */}
        <li className="flex items-start gap-3" data-id="element-524">
          <button type="button" onClick={() => updateViewing(!viewingScheduled)} className="shrink-0 mt-0.5 outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer" aria-label={viewingScheduled ? 'Bifează ca neefectuat' : 'Bifează: programează o vizionare'} data-id="element-525">
            {viewingScheduled ? <CheckCircle size={20} weight="fill" className="text-lime-1" data-id="element-526" /> : <Circle size={20} className="text-light-4" data-id="element-527" />}
          </button>
          <button type="button" onClick={() => updateViewing(!viewingScheduled)} className={cn('text-sm leading-snug text-left flex-1 cursor-pointer transition-colors', viewingScheduled ? 'text-light-3 line-through' : 'text-light-2 hover:text-light-1')} data-id="element-528">
            Programează o vizionare
            <span className="text-light-4 no-underline" data-id="element-529">
              {' '}
              · bifează după ce ați stabilit
            </span>
          </button>
        </li>

        {/* Item 4 — contract */}
        <li className="flex items-start gap-3" data-id="element-530">
          <button type="button" onClick={() => updateContract(!contractSigned)} className="shrink-0 mt-0.5 outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer" aria-label={contractSigned ? 'Bifează ca neefectuat' : 'Bifează: semnează contractul'} data-id="element-531">
            {contractSigned ? <CheckCircle size={20} weight="fill" className="text-lime-1" data-id="element-532" /> : <Circle size={20} className="text-light-4" data-id="element-533" />}
          </button>
          <button type="button" onClick={() => updateContract(!contractSigned)} className={cn('text-sm leading-snug text-left flex-1 cursor-pointer transition-colors', contractSigned ? 'text-light-3 line-through' : 'text-light-2 hover:text-light-1')} data-id="element-534">
            Semnează contractul
          </button>
        </li>

        {/* Item 5 — payment */}
        <li className="flex items-start gap-3" data-id="element-535">
          <button type="button" onClick={() => updatePayment(!paymentMade)} className="shrink-0 mt-0.5 outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer" aria-label={paymentMade ? 'Bifează ca neefectuat' : 'Bifează: transfer garanție și prima chirie'} data-id="element-536">
            {paymentMade ? <CheckCircle size={20} weight="fill" className="text-lime-1" data-id="element-537" /> : <Circle size={20} className="text-light-4" data-id="element-538" />}
          </button>
          <button type="button" onClick={() => updatePayment(!paymentMade)} className={cn('text-sm leading-snug text-left flex-1 cursor-pointer transition-colors', paymentMade ? 'text-light-3 line-through' : 'text-light-2 hover:text-light-1')} data-id="element-539">
            Transfer garanție și prima chirie
          </button>
        </li>

        {/* Item 6 — moved in */}
        <li className="flex items-start gap-3" data-id="element-540">
          <button type="button" onClick={() => updateMovedIn(!movedIn)} className="shrink-0 mt-0.5 outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer" aria-label={movedIn ? 'Bifează ca neefectuat' : 'Bifează: m-am mutat'} data-id="element-541">
            {movedIn ? <CheckCircle size={20} weight="fill" className="text-lime-1" data-id="element-542" /> : <Circle size={20} className="text-light-4" data-id="element-543" />}
          </button>
          <button type="button" onClick={() => updateMovedIn(!movedIn)} className={cn('text-sm leading-snug text-left flex-1 cursor-pointer transition-colors', movedIn ? 'text-light-3 line-through' : 'text-light-2 hover:text-light-1')} data-id="element-544">
            M-am mutat
          </button>
        </li>
      </ul>

      <div className="pt-3 mt-1 border-t border-dark-4 flex items-center justify-between" data-id="element-545">
        <p className="text-[11px] text-light-4 leading-relaxed" data-id="element-546">
          Izio nu trimite mesaje în numele tău. Tot dialogul rămâne la tine.
        </p>
        <span className="text-[11px] font-medium text-light-3 shrink-0 ml-4" data-id="element-547">
          {completedManualSteps}/5 pași completați manual
        </span>
      </div>
    </div>;
}